//Home.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  fetchJobs,
  filterJobs,
  fetchActiveJobCount,
} from '../services/jobService';
import JobCard from '../components/JobCard';
import '../index.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const taglines = [
  'Your Gateway to Career Growth.',
  'Where Talent Meets Opportunity.',
];

const filterLabels = {
  educationLevel: 'Education Level',
  jobType: 'Job Type',
  experienceLevel: 'Experience Level',
  organizationCategory: 'Organization Category',
  requiredField: 'Required Field',
};

const filterOptions = {
  educationLevel: [
    'High School',
    'Certificate',
    'Diploma',
    'Degree',
    'Postgraduate Diploma',
    'Masters Degree',
    'PhD/Doctorate',
    'Not Specified',
  ],
  jobType: ['Full-time', 'Part-time', 'Internship', 'Contract', 'Temporary', 'Attachment', 'Volunteer'],
  experienceLevel: ['Intern', 'Junior', 'Mid', 'Senior', 'Lead', 'Executive'],
  organizationCategory: [
    'Banking', 'Finance', 'Healthcare', 'Education', 'Retail', 'ICT', 'Manufacturing',
    'Construction', 'Hospitality', 'Logistics', 'Real Estate', 'Agriculture',
    'Media', 'Non-profit', 'Government',
  ],
  requiredField: [
    'Accounting & Finance',
    'Administrative & Office Support',
    'Agriculture & Environmental Sciences',
    'Architecture & Design',
    'Arts, Culture & Entertainment',
    'Business & Management',
    'Community & Social Services',
    'Construction & Skilled Trades',
    'Customer Service',
    'Data Science & Analytics',
    'Education & Teaching',
    'Engineering & Technical',
    'Healthcare & Medical',
    'Hospitality, Travel & Tourism',
    'Human Resources',
    'Information Technology',
    'Law & Legal Services',
    'Logistics, Transport & Supply Chain',
    'Manufacturing & Production',
    'Marketing, Media & Communications',
    'NGO & Nonprofit',
    'Project & Program Management',
    'Research & Development',
    'Sales & Business Development',
    'Science & Laboratory',
    'Security & Protective Services',
    'Software & Web Development',
    'Sports, Fitness & Recreation',
    'Telecommunications',
    'Training & Coaching',
    'Not Specified',
  ],
  location: [
    'Nairobi',
    'Mombasa',
    'Kisumu',
    'Nakuru',
    'Eldoret',
    'Thika',
    'Malindi',
    'Kitale',
    'Garissa',
    'Kakamega',
    'Machakos',
    'Meru',
    'Nyeri',
    'Kericho',
    'Kisii',
    'Embu',
    'Bungoma',
    'Nanyuki',
    'Isiolo',
    'Marsabit',
    'Lodwar',
    'Wajir',
    'Mandera',
    'Homa Bay',
    'Migori',
    'Voi',
    'Narok',
    'Naivasha',
    'Limuru',
    'Kiambu',
    'Murang’a',
    'Kerugoya',
    'Chuka',
    'Siaya',
    'Busia',
    'Bomet',
    'Kapenguria',
    'Kapsabet',
    'Litein',
    'Sotik',
    'Kilifi',
    'Kwale',
    'Lamu',
    'Taveta',
    'Taita',
    'Moyale',
    'Maralal',
    'Hola',
    'Othaya',
    'Ol Kalou',
    'Kajiado',
    'Loitoktok',
    'Athi River',
    'Kangundo',
    'Mwingi',
    'Wote',
    'Kitui',
    'Kathiani',
    'Makueni',
    'Vihiga (Mbale)',
    'Bondo',
    'Oyugis',
    'Awendo',
    'Kehancha',
    'Other'
  ],
};

const Home = () => {
  const [jobs, setJobs] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeJobCount, setActiveJobCount] = useState(0);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const pageSize = 10;
  const navigate = useNavigate();
  const location = useLocation();
  const loadingRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pg = parseInt(params.get('page')) || 0;
    const cat = params.get('category') || '';
    const val = params.get('value') || '';

    setPage(pg);
    setFilterCategory(cat);
    setFilterValue(val);
    loadJobs(pg, cat, val);
    loadActiveJobCount();

    const interval = setInterval(() => {
      setTaglineIndex((prev) => (prev + 1) % taglines.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const loadActiveJobCount = async () => {
    try {
      const count = await fetchActiveJobCount();
      setActiveJobCount(count);
    } catch (err) {
      console.error('Active job count error:', err);
    }
  };

  const loadJobs = async (pg, category = '', value = '') => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    const isFiltered = category && value;
    const request = isFiltered ? { [category]: value, page: pg, size: pageSize } : null;

    try {
      const data = isFiltered ? await filterJobs(request) : await fetchJobs(pg, pageSize);
      setJobs(data);
      setHasMore(data.length === pageSize);
      setPage(pg);
      navigate(`/?page=${pg}&category=${category}&value=${value}`, { replace: true });
    } catch (err) {
      console.error('Jobs fetch error:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (!filterCategory || !filterValue) return;
    loadJobs(0, filterCategory, filterValue);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  };

  const formatJobTitle = (title) => {
    return title.trim().toLowerCase().replace(/\s+/g, '-');
  };

  const handleShareAllJobs = () => {
    if (!jobs.length) return;

    const jobList = jobs.map(job => {
      return `💼 ${job.title}
  🏢 ${job.companyName}
  🗓️ Deadline: ${formatDate(job.closingDate)}
  🔗 ${window.location.origin}/job/${job.id}#${formatJobTitle(job.title)}
  `;
    }).join('\n');

    if (navigator.share) {
      navigator.share({
        title: '🚀 Job Opportunities on Notfyi',
        text: jobList,
        url: window.location.href,
      }).catch(err => {
        alert('Share cancelled or failed.');
      });
    } else {
      navigator.clipboard.writeText(jobList)
        .then(() => alert('📋 All visible jobs copied to clipboard!'))
        .catch(() => alert('❌ Failed to copy job details.'));
    }
  };

  const handleClearFilter = () => {
    setFilterCategory('');
    setFilterValue('');
    loadJobs(0);
  };

  const renderSkeletons = () => Array.from({ length: 5 }).map((_, i) => (
    <div key={i} style={{ marginBottom: 16, background: '#f0f0f0', padding: 20, borderRadius: 10 }}>
      <div className="skeleton title" />
      <div className="skeleton line" />
      <div className="skeleton line short" />
    </div>
  ));

  return (
    <div style={{ margin: 0, padding: 0, maxWidth: '100vw', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ margin: 0, padding: 0}}>
        <Header />
        {/** 
      <header style={{ width: '98%', display: 'flex', justifyContent: 'space-between', background: 'linear-gradient(to right, #00796b, #00796b, #2a5298)', color: 'white', padding: 20 }}>
        <div>
          <h3 style={{ margin: 0 }}>Active Jobs in Kenya</h3>
          <p style={{ fontStyle: 'italic' }}>{taglines[taglineIndex]}</p>
        </div>
        <div style={{ fontSize: '12px', backgroundColor: '#004d40', marginRight: 15, height: '15px' , padding: '5px 5px', borderRadius: 6, animation: 'blink 1.5s infinite' }}>
          🚀 {activeJobCount} Active Job{activeJobCount !== 1 ? 's' : ''}
        </div>
      </header>
     **/}
        <main style={{ display: 'flex', padding: '20px', flexWrap: 'wrap', boxSizing: 'border-box' }}>
          <section style={{ flex: 2, paddingRight: 20 }}>
            <form onSubmit={handleFilterSubmit} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 15, marginBottom: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
                <label>Filter By:</label>
                <select
                  value={filterCategory}
                  onChange={(e) => { setFilterCategory(e.target.value); setFilterValue(''); }}
                  style={styles.select}
                >
                  <option value="">-- Select Category --</option>
                  {Object.keys(filterOptions).map(key => (
                    <option key={key} value={key}>{filterLabels[key] || key}</option>
                  ))}

                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
                <label>Value:</label>
                <select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  disabled={!filterCategory}
                  style={styles.select}
                >
                  <option value="">-- Select Option --</option>
                  {filterCategory && filterOptions[filterCategory]?.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" style={styles.button}>Filter</button>
                <button type="button" onClick={handleClearFilter} style={styles.button}>❌ Clear</button>
              </div>
            </form>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <button onClick={handleShareAllJobs} style={styles.shareAll}>📤 Share All Jobs</button>
            </div>

            {loading ? renderSkeletons() : jobs.length === 0 ?
              (
                <div style={{
                  minHeight: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  width: '100%'
                }}>
                  <p>No jobs found.</p>
                </div>
              )
              : jobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button disabled={page === 0} onClick={() => loadJobs(page - 1, filterCategory, filterValue)} style={{ ...styles.button, opacity: page === 0 ? 0.5 : 1 }}>⬅ Prev</button>
              <span>Page {page + 1}</span>
              <button disabled={!hasMore} onClick={() => loadJobs(page + 1, filterCategory, filterValue)} style={{ ...styles.button, opacity: !hasMore ? 0.5 : 1 }}>Next ➡</button>
            </div>
          </section>

          <aside style={{ flex: 1, borderLeft: '1px solid #ccc', paddingLeft: 20, marginTop: 20 }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20
            }}>
              {/* Motivational Section */}
              <div style={{
                background: '#e8f5e9',
                borderLeft: '6px solid #2e7d32',
                padding: 16,
                borderRadius: 8
              }}>
                <h4 style={{ margin: '0 0 10px 0' }}>💡 Stay Inspired</h4>
                <p style={{ margin: 0 }}>
                  "Success is not final, failure is not fatal: It is the courage to continue that counts." – Winston Churchill
                </p>
              </div>

              {/* Notice Section */}
              <div style={{
                background: '#fffde7',
                borderLeft: '6px solid #fbc02d',
                padding: 16,
                borderRadius: 8
              }}>
                <h4 style={{ margin: '0 0 10px 0' }}>⚠️ Notice</h4>
                <p style={{ margin: 0 }}>
                  We regularly remove expired job listings to ensure you always see current and verified openings. Check back often!
                </p>
              </div>

              {/* Contact Section */}
              <div style={{
                background: '#e3f2fd',
                borderLeft: '6px solid #1976d2',
                padding: 16,
                borderRadius: 8
              }}>
                <h4 style={{ margin: '0 0 10px 0' }}>📞 Need Help?</h4>
                <p style={{ margin: 0 }}>
                  If you have questions or encounter issues, reach us at <a href="mailto:info@currentjobs.co.ke">info@currentjobs.co.ke</a>. We're here to help.
                </p>
              </div>
            </div>
          </aside>

        </main>
        <Footer />

      </div>
    </div>

  );
};

const styles = {
  select: {
    padding: '6px 10px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    backgroundColor: '#f8f9fa',
    fontSize: '14px',
    color: '#333',
  },
  button: {
    padding: '8px 14px',
    fontSize: '14px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#00796b',
    color: '#fff',
    cursor: 'pointer',
    height: '36px'
  },
  shareAll: {
    backgroundColor: '#f57c00',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 14px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  footerLink: {
    marginRight: 15,
    color: '#ffffff',
    textDecoration: 'none'
  }
};

export default Home;
